import api from '@/shared/api';
import PropTypes from 'prop-types';
import config from '@/config';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import TranslateService from '@/shared/services/TranslateService';

const retrieveTranslations = (listName, listId, fallback) => {
  if (!listName) return Promise.reject(new Error('No list name provided'));
  if (!listId) return Promise.reject(new Error('No list id provided'));

  return api.base
    .get(`${config.appUrls.api}/api/translation/list/${listName}/${listId}`)
    .then(({ data = [] }) =>
      data
        .map(el => ({
          ...el,
          value: el.isTenantLanguage && !el.value ? fallback : el.value,
          orgValue: el.value,
        }))
        .sort(dynamicSortMultiple('-isTenantLanguage', 'language')),
    );
};

const createTranslations = (listName, fallback = '') => {
  const dataCache = DataCacheService.getDataCache();
  if (!dataCache) return Promise.reject(new Error('Datacache not initialized'));
  return Promise.resolve(
    dataCache.language.sort(dynamicSortMultiple('-isDefault', 'code')).map(l => ({
      key: null,
      language: l.code,
      value: l.isDefault ? fallback || '' : '',
      orgValue: '',
      isTenantLanguage: l.isDefault,
    })),
  );
};

const saveTranslations = translations => {
  const changes = translations
    .filter(el => el.orgValue !== el.value && !!el.key)
    .map(({ orgValue, ...el }) => ({ ...el })); // Remove orgValue from object
  if (!changes || !changes.length) {
    return Promise.resolve([]);
  }
  return api.base.put(`${config.appUrls.api}/api/translation/lists/0`, changes).then(() => {
    TranslateService.reload();

    return changes;
  });
};

const updateKey = (translations, listName, listId) => {
  if (!translations || !listName || !listId) return translations;
  return translations.map(el => ({
    ...el,
    key: `${listName.toLowerCase()}_${listId.toLowerCase()}`,
  }));
};

const validateTranslations = (translations, required = false) =>
  translations &&
  !translations.some(tr => {
    if (required && !tr.key) return true;
    if (required && tr.isTenantLanguage && !tr.value) return true;
    return false;
  });

const handleTranslationChanged = (translations, language, value) =>
  translations.map(tr => (tr.language !== language ? tr : { ...tr, value }));

export default {
  // Retrieve translations from backend
  retrieve: retrieveTranslations,
  // Create new translations, for new items that don't yet exist in the backend.
  // Use the setKey function between saving the items and saving the translations.
  create: createTranslations,
  // Saves changed translations to the backend
  save: saveTranslations,
  // Updates the translation key
  setKey: updateKey,
  // Validate translations, returns true if valid
  validate: validateTranslations,

  // Helper for updating the translations in the state
  handleTranslationChanged,
  // Translation shape to use in PropTypes
  translationShape: PropTypes.shape({
    language: PropTypes.string.isRequired,
    key: PropTypes.string,
    value: PropTypes.string,
    orgValue: PropTypes.string,
    isTenantLanguage: PropTypes.bool,
  }),
};
